<script setup lang="ts">
import type { NavbarAccountView } from "@web/features/NavbarSettings/NavbarAccountMenu.vue";
import VBottomSheet from "@verbleif/lib/src/components/BottomSheet/VBottomSheet.vue";
import VIcon from "@verbleif/lib/src/components/Icon/VIcon.vue";
import MobileSheetGroup from "@verbleif/lib/src/components/MobileSheet/MobileSheetGroup.vue";
import MobileSheetRow from "@verbleif/lib/src/components/MobileSheet/MobileSheetRow.vue";
import Avatar from "@verbleif/shared/src/components/Avatar/Avatar.vue";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { ThemeSetting, useTheme } from "@verbleif/shared/src/composables/Theme/Theme";
import { useNavbarUnreadCount } from "@verbleif/shared/src/composables/useNavbarUnreadCount";
import NavbarAccountMenu from "@web/features/NavbarSettings/NavbarAccountMenu.vue";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useNavbar } from "./useNavbar";

const {
  t,
  version,
  items,
  impersonationStore,
  locationStore,
  pendingInvites,
  currentActiveTimeEntry,
  trackedTime,
  goToTimeEntry,
  chooseLocation,
  switchTheme,
  clearImpersonation,
  openDocs,
  stopAndGoToTimeEntry,
} = useNavbar();

const route = useRoute();
const { user } = useAuthStore();
const { unreadCount } = useNavbarUnreadCount();
const { theme } = useTheme();
const isDark = computed(() => theme.value === ThemeSetting.DARK);

const isMoreOpen = ref(false);
// The sheet shows either the general menu or, after tapping the account row, the account menu.
const sheetView = ref<"main" | "account">("main");
const accountView = ref<NavbarAccountView>("menu");
watch(() => route.fullPath, () => {
  isMoreOpen.value = false;
});
watch(isMoreOpen, (open) => {
  if (!open) {
    sheetView.value = "main";
    accountView.value = "menu";
  }
});

// Settings moves into the "more" sheet so the bar keeps room for the daily modules.
const tabItems = computed(() => items.value.filter(item => item.key !== "settings"));
const settingsItem = computed(() => items.value.find(item => item.key === "settings"));
const isMoreActive = computed(() => isMoreOpen.value || route.path.startsWith("/settings"));
const hasMoreBadge = computed(() => !!pendingInvites.value
  || !!currentActiveTimeEntry.value
  || impersonationStore.isImpersonating.value);

const hasStatusRows = computed(() => impersonationStore.isImpersonating.value
  || !!currentActiveTimeEntry.value
  || locationStore.hasMultipleLocations.value);

const sheetTitle = computed(() => {
  if (sheetView.value === "main") {
    return "";
  }
  switch (accountView.value) {
    case "language":
      return t("navbar.dropdown.change_language");
    case "notifications":
      return t("navbar.dropdown.notifications");
    case "inbox":
      return t("notifications.v2.inbox.title");
    default:
      return user.value?.name;
  }
});

function goBack() {
  if (accountView.value !== "menu") {
    accountView.value = "menu";
    return;
  }
  sheetView.value = "main";
}

function runAndClose(action: () => unknown) {
  isMoreOpen.value = false;
  action();
}
</script>

<template>
  <div class="md:hidden">
    <VBottomSheet
      v-model:open="isMoreOpen"
      :title="sheetTitle"
      :aria-label="t('navbar.more')"
      :show-back="sheetView === 'account'"
      offset-bottom="calc(3.5rem + env(safe-area-inset-bottom))"
      :z-index="46"
      @back="goBack"
    >
      <NavbarAccountMenu v-if="sheetView === 'account'" v-model:view="accountView" />

      <div v-else class="flex flex-col gap-4">
        <MobileSheetGroup>
          <button
            type="button"
            class="flex items-center gap-3 w-full px-4 py-3.5 text-left transition-colors active:bg-gray-100 dark:active:bg-dark-700"
            @click="sheetView = 'account'"
          >
            <Avatar
              size="md"
              main
              :name="user?.name"
              :avatar-url="user?.avatar?.contentUrl"
              :notifications="unreadCount"
            />
            <span class="min-w-0 flex-1">
              <span class="block truncate text-base font-semibold">{{ user?.name }}</span>
              <span class="block truncate text-[13px] text-gray-500 dark:text-gray-400">
                {{ locationStore.selectedLocationObject.value?.name }}
              </span>
            </span>
            <VIcon name="chevron-right" class="text-xs text-gray-300 dark:text-dark-400" />
          </button>
        </MobileSheetGroup>

        <MobileSheetGroup v-if="hasStatusRows">
          <MobileSheetRow
            v-if="impersonationStore.isImpersonating.value"
            icon="venetian-mask"
            :label="t('navbar.stop-impersonation')"
            @click="runAndClose(clearImpersonation)"
          />
          <MobileSheetRow
            v-if="currentActiveTimeEntry"
            icon="timer"
            :label="trackedTime"
            @click="runAndClose(goToTimeEntry)"
          >
            <span class="font-medium tabular-nums">{{ trackedTime }}</span>
            <template #trailing>
              <span
                class="flex items-center justify-center w-8 h-8 rounded-full bg-red-500 active:bg-red-600"
                role="button"
                @click.stop="runAndClose(stopAndGoToTimeEntry)"
              >
                <span class="w-2.5 h-2.5 bg-white rounded-xs" />
              </span>
            </template>
          </MobileSheetRow>
          <MobileSheetRow
            v-if="locationStore.hasMultipleLocations.value"
            icon="map-pin"
            :label="t('navbar.change-location')"
            :badge="pendingInvites"
            chevron
            @click="runAndClose(chooseLocation)"
          />
        </MobileSheetGroup>

        <MobileSheetGroup>
          <router-link v-if="settingsItem" v-slot="{ navigate }" custom :to="settingsItem.to">
            <MobileSheetRow
              :icon="settingsItem.icon"
              :label="settingsItem.label"
              chevron
              @click="runAndClose(navigate)"
            />
          </router-link>
          <MobileSheetRow
            icon="book"
            :label="t('navbar.manual')"
            @click="runAndClose(openDocs)"
          >
            <template #trailing>
              <VIcon name="external-link" class="text-xs text-gray-300 dark:text-dark-400" />
            </template>
          </MobileSheetRow>
          <MobileSheetRow
            icon="contrast"
            :label="t('navbar.theme')"
            @click="switchTheme"
          >
            <template #trailing>
              <span
                class="relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors"
                :class="isDark ? 'bg-primary-500' : 'bg-gray-300 dark:bg-dark-600'"
              >
                <span
                  class="inline-block h-5 w-5 rounded-full bg-white shadow transition-transform"
                  :class="isDark ? 'translate-x-[18px]' : 'translate-x-0.5'"
                />
              </span>
            </template>
          </MobileSheetRow>
        </MobileSheetGroup>

        <div class="text-center text-[11px] text-gray-400 dark:text-dark-400">
          Verbleif {{ version }}
        </div>
      </div>
    </VBottomSheet>

    <nav
      class="fixed inset-x-0 bottom-0 z-[50] flex items-stretch select-none pb-[env(safe-area-inset-bottom)]"
      :style="{ backgroundColor: 'var(--primary)' }"
    >
      <router-link v-for="item in tabItems" :key="item.key" v-slot="{ navigate, isActive }" custom :to="item.to">
        <button
          type="button"
          class="relative flex-1 min-w-0 h-14 flex flex-col items-center justify-center gap-1"
          @click="navigate"
        >
          <span v-if="isActive && !isMoreOpen" class="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] rounded-b-full bg-white" />
          <VIcon :name="item.icon" class="text-xl text-white" />
          <span class="max-w-full px-1 truncate text-[10px] leading-tight" :class="isActive && !isMoreOpen ? 'text-white font-medium' : 'text-white/70'">
            {{ item.label }}
          </span>
        </button>
      </router-link>

      <button
        type="button"
        class="relative flex-1 min-w-0 h-14 flex flex-col items-center justify-center gap-1"
        @click="isMoreOpen = !isMoreOpen"
      >
        <span v-if="isMoreActive" class="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] rounded-b-full bg-white" />
        <span class="relative">
          <VIcon name="ellipsis" class="text-xl text-white" />
          <span v-if="hasMoreBadge" class="absolute -right-1.5 -top-1 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-(--primary)" />
        </span>
        <span class="max-w-full px-1 truncate text-[10px] leading-tight" :class="isMoreActive ? 'text-white font-medium' : 'text-white/70'">
          {{ t("navbar.more") }}
        </span>
      </button>
    </nav>
  </div>
</template>
